import { useCallback, useEffect, useMemo, useState } from "react";

import useIsMounted from "@/utils/useIsMounted";
import evaluateRestQuery from "../evaluateRestQuery";

/**
 * Provides a mutate callback function for triggering a REST mutation, as well
 * as the state of the currently active mutation (if any).
 * @param {Request|String|URL|(mutationOptions: T) => (Request|string|URL)} createResource -
 * the function to create the resource (request or URL) to send
 * @param {Object} options - configurations for the REST query
 * @param {boolean} options.skip - whether or not to skip executing the query,
 *
 * @return {[mutate: Function, {data: Object, error: Error, loading: boolean}]}
 * the mutate function and the active mutation state
 */
const useRestMutation = (createResource, { skip = false } = {}) => {
  const [mutationState, setMutationState] = useState({ loading: false });
  const setMutationLoading = (bool) => {
    setMutationState((current) => {
      // If we're already in that loading state, don't change.
      if (current.loading === bool) return current;
      return {
        ...current,
        loading: bool,
      };
    });
  };

  const callMutation = useCallback(
    async (mutationOptions) => {
      // Do not execute the query if `skip` is toggled on.
      if (skip) return {};
      // If `createResource` is not a function, assume it's already a resource.
      const resource =
        typeof createResource === "function"
          ? createResource(mutationOptions)
          : createResource;
      return evaluateRestQuery(resource);
    },
    [createResource],
  );

  const mutate = useMemo(() => {
    // Each mutate function tracks whether it's stale, which will occur if a
    // newer mutate function gets defined. This is so that only the non-stale
    // mutate function affects the state.
    let mutationStale = false;

    // Each mutate function can get called multiple times. We want only the
    // most recent call to affect the state. So let's give each call its own id
    // (see `thisCallId` below) and use `mostRecentCallId` to keep track of the
    // most recent one.
    let mostRecentCallId = 0;

    const mutate = async (mutationOptions) => {
      // Each call to mutate has a unique id, so that we can check later
      // whether it's still the most recent.
      const thisCallId = ++mostRecentCallId;

      const isMutationCanceled = () => {
        // The mutation is canceled (does not trigger a state update), if it is
        // stale or not the most recent call.
        return mutationStale || thisCallId !== mostRecentCallId;
      };

      // If canceled, avoid state change.
      if (!isMutationCanceled()) {
        setMutationLoading(true);
      }

      const newMutationResult = await callMutation(mutationOptions);
      // If canceled, avoid state change.
      if (!isMutationCanceled()) {
        setMutationState({
          ...newMutationResult,
          loading: false,
        });
      }
      return newMutationResult;
    };

    // _markStale is only for internal usage.
    mutate._markStale = () => {
      mutationStale = true;
    };

    return mutate;
  }, [callMutation]);

  const isMounted = useIsMounted();
  useEffect(() => {
    return () => {
      // The current mutate function is marked as stale when there's a new
      // mutate function, or on unmount.
      mutate._markStale();
      // Also set loading to false when there's a new mutate function, but not
      // on unmount.
      if (!isMounted()) return;
      setMutationLoading(false);
    };
  }, [mutate]);

  return [mutate, mutationState];
};

export default useRestMutation;
